import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FileText, ArrowRight, Shield, KeyRound, ScrollText } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import AdminHeader from "../../components/admin/AdminHeader";
import StatsStrip from "../../components/admin/StatsStrip";
import DataTable from "../../components/admin/DataTable";
import DetailDrawer from "../../components/admin/DetailDrawer";
import { adminApi } from "../../lib/adminApi";
import { devWarn } from "../../lib/devLog";

const PAGE_SIZE = 50;

export default function ConciergeDashboard() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [authState, setAuthState] = useState("checking"); // checking|ok|nope
    const [adminEmail, setAdminEmail] = useState("");
    const [adminRole, setAdminRole] = useState("editor");
    const [ownerToast, setOwnerToast] = useState(false);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState("inquiries");

    // Route guard
    useEffect(() => {
        let cancelled = false;
        adminApi
            .meWithRetry()
            .then((me) => {
                if (cancelled) return;
                setAdminEmail(me.email);
                setAdminRole(me.role || "editor");
                setAuthState("ok");
            })
            .catch(() => {
                if (cancelled) return;
                setAuthState("nope");
                navigate("/concierge/login", { replace: true });
            });
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    // Access-denied toast (arrival from useAdminGuard's owner-required
    // bounce). Auto-clears the query param + auto-dismisses after 4.5s.
    useEffect(() => {
        if (searchParams.get("denied") === "owner-only") {
            setOwnerToast(true);
            const next = new URLSearchParams(searchParams);
            next.delete("denied");
            setSearchParams(next, { replace: true });
            const t = setTimeout(() => setOwnerToast(false), 4500);
            return () => clearTimeout(t);
        }
    }, [searchParams, setSearchParams]);

    // Stats
    const loadStats = useCallback(async () => {
        try {
            const s = await adminApi.stats();
            setStats(s);
        } catch (err) {
            if (err.status === 401) {
                navigate("/concierge/login", { replace: true });
            }
        }
    }, [navigate]);

    useEffect(() => {
        if (authState === "ok") loadStats();
    }, [authState, loadStats]);

    const onSignOut = async () => {
        try {
            await adminApi.logout();
        } catch (err) {
            // Non-blocking — the cookie will expire on its own regardless.
            devWarn("admin.logout failed; proceeding with client-side sign-out:", err);
        }
        navigate("/concierge/login", { replace: true });
    };

    if (authState !== "ok") {
        return (
            <div
                className="min-h-screen bg-[#0B0B0C] flex items-center justify-center"
                data-testid="concierge-guard-loading"
            >
                <div className="w-32 h-px bg-[rgba(201,168,106,0.3)] relative overflow-hidden">
                    <span className="absolute inset-y-0 left-0 w-1/3 bg-[#C9A86A] animate-[slide_1.2s_ease-in-out_infinite]" />
                </div>
                <style>{`
                    @keyframes slide {
                        0% { transform: translateX(-100%); }
                        100% { transform: translateX(400%); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen bg-[#0B0B0C] text-[#F3EFE7]"
            data-testid="concierge-dashboard"
        >
            <Helmet>
                <title>Concierge — ACT</title>
                <meta name="robots" content="noindex,nofollow" />
            </Helmet>

            <AdminHeader email={adminEmail} role={adminRole} onSignOut={onSignOut} />

            <main className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 md:py-10">
                {ownerToast && (
                    <div
                        role="alert"
                        className="mb-6 border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A]"
                        data-testid="dashboard-owner-only-toast"
                    >
                        Access denied — that area is owner-only. Ask an owner
                        to change your role if you need it.
                    </div>
                )}

                <StatsStrip stats={stats} />

                {/* Invoices entry tile */}
                <Link
                    to="/concierge/invoices"
                    className="mt-10 md:mt-12 flex items-center justify-between p-5 md:p-6 border border-[rgba(201,168,106,0.2)] bg-[#15161A] hover:bg-[#1C1D22] hover:border-[#C9A86A]/50 transition-all group"
                    data-testid="dashboard-invoices-tile"
                >
                    <div className="flex items-center gap-4 md:gap-5">
                        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A86A]/40 text-[#C9A86A]">
                            <FileText size={16} strokeWidth={1.4} />
                        </span>
                        <div>
                            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                Ledger
                            </div>
                            <div className="font-serif text-xl md:text-2xl text-[#F3EFE7] mt-0.5">
                                Invoices
                            </div>
                            <div className="text-[12px] text-[#9A958A] mt-1 hidden md:block">
                                Compose, dispatch, and track client invoices with a bespoke PDF trail.
                            </div>
                        </div>
                    </div>
                    <ArrowRight
                        size={18}
                        strokeWidth={1.4}
                        className="text-[#9A958A] group-hover:text-[#C9A86A] group-hover:translate-x-1 transition-all"
                    />
                </Link>

                {/* Proposals entry tile (Phase A) */}
                <Link
                    to="/concierge/proposals"
                    className="mt-4 flex items-center justify-between p-5 md:p-6 border border-[rgba(201,168,106,0.2)] bg-[#15161A] hover:bg-[#1C1D22] hover:border-[#C9A86A]/50 transition-all group"
                    data-testid="dashboard-proposals-tile"
                >
                    <div className="flex items-center gap-4 md:gap-5">
                        <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A86A]/40 text-[#C9A86A]">
                            <ScrollText size={16} strokeWidth={1.4} />
                        </span>
                        <div>
                            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                Ledger
                            </div>
                            <div className="font-serif text-xl md:text-2xl text-[#F3EFE7] mt-0.5">
                                Proposals
                            </div>
                            <div className="text-[12px] text-[#9A958A] mt-1 hidden md:block">
                                Compose bespoke proposals with AI-assisted copy, signature capture, and a 4-page PDF trail.
                            </div>
                        </div>
                    </div>
                    <ArrowRight
                        size={18}
                        strokeWidth={1.4}
                        className="text-[#9A958A] group-hover:text-[#C9A86A] group-hover:translate-x-1 transition-all"
                    />
                </Link>

                {/* Access entry tile — owner-only */}
                {adminRole === "owner" && (
                    <Link
                        to="/concierge/access/users"
                        className="mt-4 flex items-center justify-between p-5 md:p-6 border border-[rgba(201,168,106,0.2)] bg-[#15161A] hover:bg-[#1C1D22] hover:border-[#C9A86A]/50 transition-all group"
                        data-testid="dashboard-access-tile"
                    >
                        <div className="flex items-center gap-4 md:gap-5">
                            <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A86A]/40 text-[#C9A86A]">
                                <Shield size={16} strokeWidth={1.4} />
                            </span>
                            <div>
                                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                    Access · Owner
                                </div>
                                <div className="font-serif text-xl md:text-2xl text-[#F3EFE7] mt-0.5">
                                    Admins & Passcodes
                                </div>
                                <div className="text-[12px] text-[#9A958A] mt-1 hidden md:block">
                                    Manage concierge accounts and private-portfolio passcodes.
                                </div>
                            </div>
                        </div>
                        <ArrowRight
                            size={18}
                            strokeWidth={1.4}
                            className="text-[#9A958A] group-hover:text-[#C9A86A] group-hover:translate-x-1 transition-all"
                        />
                    </Link>
                )}

                {/* Passcodes-only entry — visible to editors (owners already
                    reach passcodes through the Admins & Passcodes tile). */}
                {adminRole !== "owner" && (
                    <Link
                        to="/concierge/access/passcodes"
                        className="mt-4 flex items-center justify-between p-5 md:p-6 border border-[rgba(201,168,106,0.2)] bg-[#15161A] hover:bg-[#1C1D22] hover:border-[#C9A86A]/50 transition-all group"
                        data-testid="dashboard-passcodes-tile"
                    >
                        <div className="flex items-center gap-4 md:gap-5">
                            <span className="inline-flex items-center justify-center w-11 h-11 border border-[#C9A86A]/40 text-[#C9A86A]">
                                <KeyRound size={16} strokeWidth={1.4} />
                            </span>
                            <div>
                                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A86A]">
                                    Access
                                </div>
                                <div className="font-serif text-xl md:text-2xl text-[#F3EFE7] mt-0.5">
                                    Private Passcodes
                                </div>
                                <div className="text-[12px] text-[#9A958A] mt-1 hidden md:block">
                                    Issue and revoke private-portfolio access codes.
                                </div>
                            </div>
                        </div>
                        <ArrowRight
                            size={18}
                            strokeWidth={1.4}
                            className="text-[#9A958A] group-hover:text-[#C9A86A] group-hover:translate-x-1 transition-all"
                        />
                    </Link>
                )}

                <div className="mt-10 md:mt-12">
                    <Tabs
                        value={activeTab}
                        onValueChange={setActiveTab}
                        className="w-full"
                    >
                        <TabsList
                            className="bg-[#15161A] border border-[rgba(201,168,106,0.2)] h-auto p-1 rounded-none gap-0"
                            data-testid="admin-tabs-list"
                        >
                            {[
                                { value: "inquiries", label: "Inquiries" },
                                { value: "callbacks", label: "Callbacks" },
                                { value: "newsletter", label: "Newsletter" },
                                { value: "nda_access", label: "Private Access" },
                            ].map((t) => (
                                <TabsTrigger
                                    key={t.value}
                                    value={t.value}
                                    className="px-5 py-2 rounded-none text-[11px] tracking-[0.22em] uppercase text-[#9A958A] data-[state=active]:bg-[#C9A86A]/10 data-[state=active]:text-[#C9A86A] data-[state=active]:shadow-none hover:text-[#F3EFE7] transition-colors"
                                    data-testid={`admin-tab-${t.value}`}
                                >
                                    {t.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        <TabsContent value="inquiries" className="mt-8">
                            <InquiriesTab onAfterMutate={loadStats} />
                        </TabsContent>
                        <TabsContent value="callbacks" className="mt-8">
                            <CallbacksTab onAfterMutate={loadStats} />
                        </TabsContent>
                        <TabsContent value="newsletter" className="mt-8">
                            <NewsletterTab />
                        </TabsContent>
                        <TabsContent value="nda_access" className="mt-8">
                            <NdaAccessTab />
                        </TabsContent>
                    </Tabs>
                </div>
            </main>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Inquiries tab
// ---------------------------------------------------------------------------
const INQUIRY_COLUMNS = [
    {
        key: "created_at",
        label: "Date",
        render: (r) => formatDate(r.created_at),
        className: "w-[170px] tabular-nums font-mono text-xs",
    },
    { key: "full_name", label: "Name" },
    {
        key: "email",
        label: "Email",
        render: (r) => <span className="text-[#9A958A]">{r.email}</span>,
    },
    {
        key: "project_type",
        label: "Project",
        render: (r) => (
            <span className="text-[#9A958A] text-[13px]">{r.project_type}</span>
        ),
    },
    {
        key: "location",
        label: "Location",
        render: (r) => (
            <span className="text-[#9A958A] text-[13px]">{r.location}</span>
        ),
    },
    {
        key: "budget",
        label: "Budget",
        render: (r) => (
            <span className="text-[#9A958A] text-[13px]">{r.budget || "—"}</span>
        ),
    },
    {
        key: "timeline",
        label: "Timeline",
        render: (r) => (
            <span className="text-[#9A958A] text-[13px]">{r.timeline}</span>
        ),
    },
];

function InquiriesTab({ onAfterMutate }) {
    return (
        <ResourceTab
            kind="inquiries"
            testIdPrefix="admin-inquiries"
            columns={INQUIRY_COLUMNS}
            supportsHandled
            list={adminApi.listInquiries}
            update={adminApi.updateInquiry}
            detailFields={[
                { key: "full_name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "project_type", label: "Project Type" },
                { key: "location", label: "Location" },
                { key: "budget", label: "Budget" },
                { key: "timeline", label: "Timeline" },
                { key: "message", label: "Message", multiline: true },
                { key: "ip", label: "IP", muted: true },
                { key: "user_agent", label: "User Agent", muted: true, multiline: true },
            ]}
            onAfterMutate={onAfterMutate}
        />
    );
}

// ---------------------------------------------------------------------------
// Callbacks tab
// ---------------------------------------------------------------------------
const CALLBACK_COLUMNS = [
    {
        key: "created_at",
        label: "Date",
        render: (r) => formatDate(r.created_at),
        className: "w-[170px] tabular-nums font-mono text-xs",
    },
    { key: "full_name", label: "Name" },
    {
        key: "phone",
        label: "Phone",
        render: (r) => (
            <span className="text-[#F3EFE7] tabular-nums">{r.phone}</span>
        ),
    },
    {
        key: "email",
        label: "Email",
        render: (r) => (
            <span className="text-[#9A958A]">{r.email || "—"}</span>
        ),
    },
    {
        key: "window",
        label: "Day / Time",
        render: (r) => (
            <span className="text-[#9A958A] text-[13px]">
                {r.day_window} · {r.time_window}
            </span>
        ),
    },
];

function CallbacksTab({ onAfterMutate }) {
    return (
        <ResourceTab
            kind="callbacks"
            testIdPrefix="admin-callbacks"
            columns={CALLBACK_COLUMNS}
            supportsHandled
            list={adminApi.listCallbacks}
            update={adminApi.updateCallback}
            detailFields={[
                { key: "full_name", label: "Name" },
                { key: "phone", label: "Phone" },
                { key: "email", label: "Email" },
                { key: "day_window", label: "Day Window" },
                { key: "time_window", label: "Time Window" },
                { key: "note", label: "Note", multiline: true },
                { key: "ip", label: "IP", muted: true },
                { key: "user_agent", label: "User Agent", muted: true, multiline: true },
            ]}
            onAfterMutate={onAfterMutate}
        />
    );
}

// ---------------------------------------------------------------------------
// Newsletter tab (read-only)
// ---------------------------------------------------------------------------
const NEWSLETTER_COLUMNS = [
    {
        key: "created_at",
        label: "Date",
        render: (r) => formatDate(r.created_at),
        className: "w-[180px] tabular-nums font-mono text-xs",
    },
    { key: "email", label: "Email" },
    {
        key: "ip",
        label: "IP",
        render: (r) => (
            <span className="text-[#6e6a62] tabular-nums font-mono text-xs">
                {r.ip}
            </span>
        ),
    },
];

function NewsletterTab() {
    return (
        <ResourceTab
            kind="newsletter"
            testIdPrefix="admin-newsletter"
            columns={NEWSLETTER_COLUMNS}
            list={adminApi.listNewsletter}
            readOnly
        />
    );
}

// ---------------------------------------------------------------------------
// NDA access tab (read-only)
// ---------------------------------------------------------------------------
const NDA_ACCESS_COLUMNS = [
    {
        key: "accessed_at",
        label: "Date",
        render: (r) => formatDate(r.accessed_at),
        className: "w-[170px] tabular-nums font-mono text-xs",
    },
    { key: "full_name", label: "Name" },
    {
        key: "email",
        label: "Email",
        render: (r) => <span className="text-[#9A958A]">{r.email}</span>,
    },
    {
        key: "ip",
        label: "IP",
        render: (r) => (
            <span className="text-[#6e6a62] tabular-nums font-mono text-xs">
                {r.ip}
            </span>
        ),
    },
    {
        key: "user_agent",
        label: "User Agent",
        render: (r) => (
            <span
                className="text-[#6e6a62] text-xs truncate block max-w-[240px]"
                title={r.user_agent}
            >
                {r.user_agent}
            </span>
        ),
    },
    {
        key: "nda_accepted",
        label: "NDA",
        render: () => (
            <span className="text-[#C9A86A] text-[11px] tracking-[0.22em] uppercase">
                ✓ Accepted
            </span>
        ),
        className: "w-[110px]",
    },
];

function NdaAccessTab() {
    return (
        <ResourceTab
            kind="nda-access"
            testIdPrefix="admin-nda-access"
            columns={NDA_ACCESS_COLUMNS}
            list={adminApi.listNdaAccess}
            readOnly
        />
    );
}

// ---------------------------------------------------------------------------
// Shared resource tab — search/filter/table/drawer/pagination
// ---------------------------------------------------------------------------
function ResourceTab({
    kind,
    testIdPrefix,
    columns,
    supportsHandled = false,
    readOnly = false,
    list,
    update,
    detailFields,
    onAfterMutate,
}) {
    const [q, setQ] = useState("");
    const [qInput, setQInput] = useState("");
    const [unhandledOnly, setUnhandledOnly] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [csvDownloading, setCsvDownloading] = useState(false);

    // Debounce search input → q
    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setQ(qInput), 300);
        return () => clearTimeout(debounceRef.current);
    }, [qInput]);

    // Reset offset when filters change
    useEffect(() => {
        setOffset(0);
    }, [q, unhandledOnly]);

    const load = useCallback(
        async (nextOffset) => {
            setLoading(true);
            setError("");
            try {
                const params = { q, limit: PAGE_SIZE, offset: nextOffset };
                if (supportsHandled && unhandledOnly) params.handled = false;
                const res = await list(params);
                setItems((prev) =>
                    nextOffset === 0 ? res.items : [...prev, ...res.items],
                );
                setTotal(res.total);
                setOffset(nextOffset);
            } catch (err) {
                setError(err.message || "Failed to load.");
            } finally {
                setLoading(false);
            }
        },
        [q, unhandledOnly, supportsHandled, list],
    );

    useEffect(() => {
        load(0);
    }, [load]);

    const onRowView = (row) => {
        setSelected(row);
        setDrawerOpen(true);
    };

    const onSaveRow = async ({ handled, notes }) => {
        if (!selected) return;
        const updated = await update(selected.id, { handled, notes });
        setItems((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelected(updated);
        if (onAfterMutate) onAfterMutate();
    };

    const onToggleHandled = async (row) => {
        if (readOnly || !update) return;
        const updated = await update(row.id, { handled: !row.handled });
        setItems((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        if (selected && selected.id === updated.id) setSelected(updated);
        if (onAfterMutate) onAfterMutate();
    };

    const onExport = async () => {
        setCsvDownloading(true);
        try {
            const opts = { q };
            if (supportsHandled && unhandledOnly) opts.handled = false;
            await adminApi.downloadCsv(kind, opts);
        } catch (err) {
            setError(err.message || "Export failed.");
        } finally {
            setCsvDownloading(false);
        }
    };

    return (
        <div data-testid={`${testIdPrefix}-panel`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-5">
                <input
                    type="search"
                    placeholder="Search…"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    className="flex-1 bg-[#15161A] border border-[rgba(201,168,106,0.2)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-sm py-2.5 px-4 transition-colors duration-300 placeholder:text-[#6e6a62]"
                    data-testid={`${testIdPrefix}-search`}
                />
                {supportsHandled && (
                    <label
                        className="inline-flex items-center gap-3 cursor-pointer text-[11px] tracking-[0.22em] uppercase text-[#9A958A] select-none"
                        data-testid={`${testIdPrefix}-unhandled-toggle-label`}
                    >
                        <input
                            type="checkbox"
                            checked={unhandledOnly}
                            onChange={(e) => setUnhandledOnly(e.target.checked)}
                            className="w-4 h-4 accent-[#C9A86A] cursor-pointer"
                            data-testid={`${testIdPrefix}-unhandled-toggle`}
                        />
                        Unhandled only
                    </label>
                )}
                <button
                    type="button"
                    onClick={onExport}
                    disabled={csvDownloading}
                    className="btn-gold-outline text-[11px] tracking-[0.22em] uppercase px-5 py-2.5 disabled:opacity-70 disabled:cursor-wait"
                    data-testid={`${testIdPrefix}-export`}
                >
                    {csvDownloading ? "Exporting…" : "Export CSV"}
                </button>
            </div>

            {error && (
                <div
                    role="alert"
                    className="mb-4 border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A]"
                    data-testid={`${testIdPrefix}-error`}
                >
                    {error}
                </div>
            )}

            <DataTable
                columns={columns}
                rows={items}
                loading={loading}
                onViewRow={readOnly ? null : onRowView}
                onToggleHandled={
                    supportsHandled && !readOnly ? onToggleHandled : null
                }
                supportsHandled={supportsHandled && !readOnly}
                testIdPrefix={testIdPrefix}
                emptyLabel={`No ${kind} yet.`}
            />

            <div className="mt-6 flex items-center justify-between text-[11px] tracking-[0.22em] uppercase text-[#9A958A]">
                <span data-testid={`${testIdPrefix}-count`}>
                    Showing {items.length} of {total}
                </span>
                {items.length < total && (
                    <button
                        type="button"
                        onClick={() => load(offset + PAGE_SIZE)}
                        disabled={loading}
                        className="text-[#C9A86A] hover:text-[#D9BC80] transition-colors disabled:opacity-50"
                        data-testid={`${testIdPrefix}-load-more`}
                    >
                        {loading ? "Loading…" : "Load more →"}
                    </button>
                )}
            </div>

            {!readOnly && (
                <DetailDrawer
                    open={drawerOpen}
                    onOpenChange={setDrawerOpen}
                    record={selected}
                    fields={detailFields}
                    onSave={onSaveRow}
                    testIdPrefix={testIdPrefix}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
